import { useEffect, useState } from "react";
import { fetchedAllPaymentsByOperation } from "../../../apis/apis.accounting/apisCash";
import ReactHTMLTableToExcel from "react-html-table-to-excel";

// ── helpers ─────────────────────────────────────────────────────────────────
const num = (val) => Number(val || 0).toFixed(2);

// ── clases base ──────────────────────────────────────────────────────────────
const thBase =
    "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600";
const tdBase =
    "px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle";
const tdMoney = `${tdBase} text-right font-semibold`;

// Paleta de columnas financieras
const COL = {
    cyan:   { th: `${thBase} bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200 border-l-2 border-cyan-300 dark:border-cyan-600`,     td: `${tdMoney} bg-cyan-50 dark:bg-cyan-900/20 text-cyan-800 dark:text-cyan-200 border-l-2 border-cyan-200 dark:border-cyan-700` },
    amber:  { th: `${thBase} bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-l-2 border-amber-300 dark:border-amber-600`, td: `${tdMoney} bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 border-l-2 border-amber-200 dark:border-amber-700` },
    pink:   { th: `${thBase} bg-pink-100 dark:bg-pink-900/40 text-pink-800 dark:text-pink-200 border-l-2 border-pink-300 dark:border-pink-600`,       td: `${tdMoney} bg-pink-50 dark:bg-pink-900/20 text-pink-800 dark:text-pink-200 border-l-2 border-pink-200 dark:border-pink-700` },
    purple: { th: `${thBase} bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200 border-l-2 border-purple-300 dark:border-purple-600`, td: `${tdMoney} bg-purple-50 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 border-l-2 border-purple-200 dark:border-purple-700` },
    gray:   { th: `${thBase} bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-l-2 border-gray-300 dark:border-gray-600`,           td: `${tdMoney} bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-l-2 border-gray-200 dark:border-gray-700` },
    green:  { th: `${thBase} bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border-l-2 border-emerald-300 dark:border-emerald-600`, td: `${tdMoney} bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 border-l-2 border-emerald-200 dark:border-emerald-700` },
};

function rowClass(item) {
    if (item.operationStatus === "03") return "opacity-60 bg-red-50 dark:bg-red-900/10";
    if (item.operationType === "01")   return "bg-amber-50 dark:bg-amber-900/10";
    return "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors";
}

export function CollectionSheetList({ sales, setCashFlow, setCashFlowSet, setIsModalOpen }) {
    const [summary, setSummary] = useState({
        sumTotalPurchased: 0,
        sumTotalPaid: 0,
        sumTotalPaidInCash: 0,
        sumTotalPaidInYape: 0,
        sumTotalReturned: 0,
        sumTotalPending: 0,
        sumTotalInCredit: 0,
    });

    useEffect(() => {
        const active = sales?.filter((i) => i.operationStatus === "02") ?? [];
        const activeOrRet = sales?.filter(
            (i) => i.operationStatus === "02" || i.operationStatus === "03"
        ) ?? [];
        const sum = (arr, key) => arr.reduce((t, o) => t + Number(o[key] || 0), 0);

        setSummary({
            sumTotalPurchased:  sum(active, "totalPurchased"),
            sumTotalPaid:       sum(active, "totalPaid"),
            sumTotalPaidInCash: sum(active, "totalPaidInCash"),
            sumTotalPaidInYape: sum(active, "totalPaidInYape"),
            sumTotalReturned:   sum(activeOrRet, "totalReturned"),
            sumTotalInCredit:   sum(active, "totalInCredit"),
            sumTotalPending:    sum(active, "totalPending"),
        });
    }, [sales]);

    const getAllPayments = async (operationID) => {
        try {
            const response = await fetchedAllPaymentsByOperation({ operationID });
            setCashFlowSet(response);
        } catch (error) { throw error; }
    };

    const handlePayClick = (item) => {
        setCashFlow((prev) => ({
            ...prev,
            description: "",
            type: "E",
            operationID: Number(item.operationID),
            total: Number(item.totalPending),
        }));
        getAllPayments(item.operationID);
        setIsModalOpen(true);
    };

    const hasPending = (item) => Number(item.totalInCredit) > 0;

    if (!sales || sales.length === 0) {
        return (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                <div className="py-16 text-center">
                    <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                        <i className="fas fa-file-invoice text-gray-400 dark:text-gray-500 text-2xl"></i>
                    </div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Sin resultados</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Seleccione un vendedor y aplique el filtro para ver la hoja de cobranza.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">

            {/* Card header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                        Hoja de cobranza
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {sales.length} registros encontrados
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="hidden lg:flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-cyan-300 dark:bg-cyan-600"></span>Comprado</span>
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-300 dark:bg-amber-600"></span>Saldo</span>
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-pink-300 dark:bg-pink-600"></span>Crédito</span>
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-300 dark:bg-emerald-600"></span>Efectivo</span>
                    </div>
                    <ReactHTMLTableToExcel
                        id="table-xls-button"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-medium px-3 py-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                        table="table-to-xls"
                        filename="ReportePagos"
                        sheet="report"
                        buttonText="↓ Exportar XLS"
                    />
                </div>
            </div>

            {/* ── Desktop table ── */}
            <div className="hidden md:block overflow-x-auto max-h-[70vh] overflow-y-auto bg-white dark:bg-gray-900">
                <table className="min-w-full text-sm border-separate border-spacing-0" id="table-to-xls">
                    <thead className="sticky top-0 z-10">
                        <tr>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>ID</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Fecha</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Hora</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Usuario</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Código</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Cliente</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Visita</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Ruta</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Estado</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Sis. Dist.</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Dirección</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Serie</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Núm.</th>
                            <th className={COL.cyan.th}><i className="fas fa-shopping-cart mr-1"></i>Comprado</th>
                            <th className={COL.amber.th}><i className="fas fa-balance-scale mr-1"></i>Saldo</th>
                            <th className={COL.pink.th}><i className="fas fa-credit-card mr-1"></i>Crédito</th>
                            <th className={COL.purple.th}><i className="fas fa-mobile-alt mr-1"></i>Yape</th>
                            <th className={COL.gray.th}><i className="fas fa-undo mr-1"></i>Devuelto</th>
                            <th className={COL.green.th}><i className="fas fa-money-bill-wave mr-1"></i>Efectivo</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sales.map((item, index) => (
                            <tr key={index} className={rowClass(item)}>
                                <td className={`${tdBase} text-blue-600 dark:text-blue-400 font-medium`}>{item.operationID}</td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>{item.registerDate}</td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>{item.registerTime}</td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>{item.userName?.substring(0, 5)}...</td>
                                <td className={`${tdBase} text-gray-700 dark:text-gray-300`}>{item.clientObservation}</td>
                                <td className={`${tdBase} font-medium text-gray-800 dark:text-gray-200`}>{item.clientName}</td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>{item.clientVisitDayDisplay}</td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>{item.clientGangName}</td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>{item.dailyRouteDisplayStatus}</td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>{item.clientPhysicalDistributionDisplay}</td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>{item.clientAddress}</td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>{item.receiptSerial}</td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>{item.receiptNumber}</td>
                                <td className={COL.cyan.td}>{num(item.totalPurchased)}</td>
                                <td className={COL.amber.td}>{num(item.totalPending)}</td>
                                <td className={COL.pink.td}>{num(item.totalInCredit)}</td>
                                <td className={COL.purple.td}>{num(item.totalPaidInYape)}</td>
                                <td className={COL.gray.td}>{num(item.totalReturned)}</td>
                                <td className={COL.green.td}>{num(item.totalPaidInCash)}</td>
                                <td className={`${tdBase} text-center`}>
                                    {item.operationStatus === "02" && (
                                        <button
                                            type="button"
                                            onClick={() => handlePayClick(item)}
                                            className={
                                                hasPending(item)
                                                    ? "inline-flex items-center gap-1 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-2.5 py-1 transition-colors"
                                                    : "inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-medium px-2.5 py-1 transition-colors"
                                            }
                                        >
                                            {hasPending(item) ? "PAGAR" : "VER"}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-gray-800 dark:bg-gray-950">
                            <td colSpan={10} className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 border-t border-gray-600">
                                {sales.length} registros
                            </td>
                            <td colSpan={3} className="px-3 py-2 text-xs font-bold text-right text-white uppercase tracking-wide border-t border-gray-600">
                                <i className="fas fa-calculator mr-2"></i>TOTAL
                            </td>
                            <td className={`${tdMoney} bg-cyan-900/60 text-cyan-200 border-l-2 border-cyan-600 border-t border-gray-600`}>{num(summary.sumTotalPurchased)}</td>
                            <td className={`${tdMoney} bg-amber-900/60 text-amber-200 border-l-2 border-amber-600 border-t border-gray-600`}>{num(summary.sumTotalPending)}</td>
                            <td className={`${tdMoney} bg-pink-900/60 text-pink-200 border-l-2 border-pink-600 border-t border-gray-600`}>{num(summary.sumTotalInCredit)}</td>
                            <td className={`${tdMoney} bg-purple-900/60 text-purple-200 border-l-2 border-purple-600 border-t border-gray-600`}>{num(summary.sumTotalPaidInYape)}</td>
                            <td className={`${tdMoney} bg-gray-700 text-gray-200 border-l-2 border-gray-500 border-t border-gray-600`}>{num(summary.sumTotalReturned)}</td>
                            <td className={`${tdMoney} bg-emerald-900/60 text-emerald-200 border-l-2 border-emerald-600 border-t border-gray-600`}>{num(summary.sumTotalPaidInCash)}</td>
                            <td className="border-t border-gray-600 bg-gray-800 dark:bg-gray-950"></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* ── Mobile cards ── */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
                {sales.map((item, index) => (
                    <div key={index} className={`p-4 ${rowClass(item)}`}>
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {item.clientName}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {item.clientObservation} · {item.clientGangName} · {item.registerDate}
                                </p>
                            </div>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                item.operationStatus === "03"
                                    ? "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
                                    : item.operationType === "01"
                                      ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                                      : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                            }`}>
                                {item.operationStatus === "03" ? "ANULADO"
                                    : item.operationType === "01" ? "COTIZACIÓN"
                                    : "COMPROBANTE"}
                            </span>
                        </div>

                        {/* Doc info */}
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            <i className="fas fa-receipt mr-1"></i>
                            {item.receiptSerial} - {item.receiptNumber} · {item.registerTime}
                        </p>

                        {/* Amounts */}
                        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                            {[
                                { label: "Comprado", val: item.totalPurchased, color: "text-cyan-600 dark:text-cyan-400" },
                                { label: "Saldo",    val: item.totalPending,   color: "text-amber-600 dark:text-amber-400" },
                                { label: "Crédito",  val: item.totalInCredit,  color: "text-pink-600 dark:text-pink-400" },
                                { label: "Yape",     val: item.totalPaidInYape, color: "text-purple-600 dark:text-purple-400" },
                                { label: "Devuelto", val: item.totalReturned,  color: "text-gray-600 dark:text-gray-400" },
                                { label: "Efectivo", val: item.totalPaidInCash, color: "text-emerald-600 dark:text-emerald-400" },
                            ].map(({ label, val, color }) => (
                                <div key={label} className="flex justify-between items-center bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-1.5">
                                    <span className="text-gray-500 dark:text-gray-400">{label}</span>
                                    <span className={`font-semibold ${color}`}>{num(val)}</span>
                                </div>
                            ))}
                        </div>

                        {/* Action */}
                        {item.operationStatus === "02" && (
                            <button
                                type="button"
                                onClick={() => handlePayClick(item)}
                                className={`w-full rounded-lg text-sm font-medium py-2 transition-colors ${
                                    hasPending(item)
                                        ? "bg-red-600 hover:bg-red-700 text-white"
                                        : "border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                }`}
                            >
                                <i className={`fas ${hasPending(item) ? "fa-credit-card" : "fa-eye"} mr-2`}></i>
                                {hasPending(item) ? "PAGAR" : "VER DETALLES"}
                            </button>
                        )}
                    </div>
                ))}

                {/* Mobile totals */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50">
                    <p className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                        <i className="fas fa-calculator mr-2"></i>Resumen de totales
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        {[
                            { label: "Total Comprado", val: summary.sumTotalPurchased, color: "text-cyan-600 dark:text-cyan-400" },
                            { label: "Total Saldo",    val: summary.sumTotalPending,   color: "text-amber-600 dark:text-amber-400" },
                            { label: "Total Crédito",  val: summary.sumTotalInCredit,  color: "text-pink-600 dark:text-pink-400" },
                            { label: "Total Yape",     val: summary.sumTotalPaidInYape, color: "text-purple-600 dark:text-purple-400" },
                            { label: "Total Devuelto", val: summary.sumTotalReturned,  color: "text-gray-600 dark:text-gray-400" },
                            { label: "Total Efectivo", val: summary.sumTotalPaidInCash, color: "text-emerald-600 dark:text-emerald-400" },
                        ].map(({ label, val, color }) => (
                            <div key={label} className="flex justify-between items-center bg-white dark:bg-gray-700 rounded-lg px-3 py-1.5 border border-gray-200 dark:border-gray-600">
                                <span className="text-gray-500 dark:text-gray-400">{label}</span>
                                <span className={`font-semibold ${color}`}>{num(val)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
